<template>
    <div class="mt-3">
        <rounded-container variant="dark">
            <actions 
                @suggest-buy-symbol="suggestBuy"
                />

            <div v-for="(byAsset, rowIndex) in sellableAssets" :key="byAsset.symbol" :class="actionRowClass(rowIndex)" class="p-3">
                
                <b-row>
                    <b-col cols="8">
                        <h4>
                            <asset-symbol :asset="byAsset.asset" /> <span>${{ formatPrice(byAsset.asset.price) }}</span>
                            <span class="text-secondary"> {{ byAsset.count }}</span>
                        </h4>
                    </b-col>
                    <b-col class="text-right">
                        <!-- Daca exista 3+ ITM, atunci calculat ca pot sa fac exit -->
                        <b-button v-if="byAsset.minItm >= 3" variant="success" size="sm" @click.prevent="openProcessExit(byAsset.asset, byAsset.positions)">
                            <strong>${{ formatPrice(suggestedExitAmount(byAsset.count)) }}</strong>
                        </b-button>
                        <!-- Daca exista 0-2 ITM, atunci buton de REINVEST pentru ca situatie -->
                        <b-button v-else variant="warning" size="sm" @click.prevent="openProcessExit(byAsset.asset, byAsset.positions)">
                            REINVEST
                        </b-button>
                    </b-col>
                </b-row>
                
                <div v-if="byAsset.asset.meta && byAsset.asset.meta.oneLiner && byAsset.minItm >= 3" class="text-info">
                    {{ byAsset.asset.meta.oneLiner }}
                </div>

                <compact-position-list :positionList="byAsset.positions" :totalCount="byAsset.count" class="pt-2" />
            </div>

            <div v-if="sellableAssets.length && assetsToBuy.length" class="text-center bg-dark">
                - - -
            </div>

            <div class="p-3 under-water-asset" v-for="(oneAsset, rowIndex) in assetsToBuy" :key="oneAsset.symbol" :class="actionRowClass(rowIndex)">
                <b-row>
                    <b-col cols="8">
                        <h4>
                            <asset-symbol :asset="oneAsset.asset" /> <span>${{ formatPrice(oneAsset.asset.price) }}</span>
                        </h4>
                    </b-col>
                    <b-col class="text-right">
                        <b-button variant="danger" :class="buyButtonClass(oneAsset.asset)" size="sm" @click.prevent="suggestBuy(oneAsset.symbol)">
                            BUY ${{ buyButtonLabel(oneAsset.asset) }}
                        </b-button>
                    </b-col>
                </b-row>

                <b-row class="small pt-2">
                    <b-col class="formatPrice">{{ formatTimeFromISO(oneAsset.lowestPosition.open_time) }}</b-col>

                    <b-col class="formatPrice text-white" v-if="oneAsset.indicator > -5">{{ Math.round(oneAsset.indicator * 100) / 100 }}%</b-col>
                    <b-col class="formatPrice text-warning" v-else-if="oneAsset.indicator > -10">{{ Math.round(oneAsset.indicator * 100) / 100 }}%</b-col>
                    <b-col class="formatPrice text-danger" v-else>{{ Math.round(oneAsset.indicator * 100) / 100 }}%</b-col>

                    <b-col class="formatPrice" cols="5">{{ formatPrice(oneAsset.lowestPosition.open_price) }} &#x21E2; {{ formatPrice(oneAsset.lowestPosition.close_price) }}</b-col>
                </b-row>

                <div v-if="oneAsset.asset.meta && oneAsset.asset.meta.oneLiner" class="text-secondary pt-1">
                    {{ oneAsset.asset.meta.oneLiner }}
                </div>
            </div>
        </rounded-container>
    </div>
</template>

<script>

import formatter from '../mixins/formatter'

import { mapState, mapGetters } from "vuex";

import Actions from './Actions.vue';
import RoundedContainer from './style/RoundedContainer.vue';
import AssetSymbol from './style/AssetSymbol.vue';

import ProcessExit from '../components/overlays/ProcessExit.vue'
import CompactPositionList from '@/components/asset/partials/CompactPositionList.vue';

import { SELL_INDICATOR_THRESHOLD, MARKET_DEFAULT_BUY_POSITIONS } from '@/constants/trading';
import { SHEET_STYLE } from '@/constants/ui';

export default {
    name: "Positions",
    components: {
        Actions,
        RoundedContainer,
        AssetSymbol,
        CompactPositionList,
    },
    mixins: [formatter],
    methods: {
        getAssetBySymbol(symbol) {
            //console.log(this.$store)
            return this.$store.getters.getAssetBySymbol(symbol);
        },
        actionRowClass(rowIndex) {
            if (rowIndex % 2) return 'oddRow';

            return 'evenRow'
        },
        suggestBuy(symbol) {
            this.$emit('suggest-buy', symbol);
        },
        processMinPositionsItm(currentPrice, lowestPrices) {
            let count = 0;
            for (let i=0; i<lowestPrices.length; i++) {
                if (lowestPrices[i] < currentPrice) count++;
            }
            return count;
        },
        openProcessExit(asset, positions) {
            this.$FModal.show(
                { 
                    component: ProcessExit, 
                    ...SHEET_STYLE
                }, 
                { 
                    currentAsset: asset,
                    positionList: positions
                }
            )
        },
        suggestedExitAmount(count) {
            // sell about half, rounding up so an odd count still pairs off
            return Math.ceil(count / 2) * this.defaultPositionSize;
        },
        buyButtonLabel(asset) {
            return this.$store.getters.resolveBuySize(asset).positions * this.defaultPositionSize;
        },
        buyButtonClass(asset) {
            const resolved = this.$store.getters.resolveBuySize(asset);
            if (resolved.rule) return 'buy-btn-rule';

            const amount = resolved.positions * this.defaultPositionSize;
            if (amount > 500) return 'buy-btn-critical';
            const defaultposNo = MARKET_DEFAULT_BUY_POSITIONS[asset.market] ?? 1;
            const defaultAmount = defaultposNo * this.defaultPositionSize;
            if (amount > defaultAmount) return 'buy-btn-elevated';
            return '';
        }
    },
    computed: {
        ...mapState(['userData']),
        ...mapState({
            positions: state => state.positions,
            actionableCounts: state => state.positions.actionableCounts,
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        ...mapGetters(['assetsToBuy']),
        sellableAssets() {
            let actionablePositions = this.positions.actionablePositions;
            let byAsset = {}
            for (let i=0; i<actionablePositions.length; i++) {
                if (actionablePositions[i].indicator >= SELL_INDICATOR_THRESHOLD) {
                    if (actionablePositions[i].ticker in byAsset) {
                        byAsset[actionablePositions[i].ticker].push(actionablePositions[i])
                    }
                    else {
                        byAsset[actionablePositions[i].ticker] = [actionablePositions[i]]
                    }
                }
            }

            let returnData = [];
            for (let symbol of Object.keys(byAsset)) {
                let currentAsset = this.getAssetBySymbol(symbol);
                if (!currentAsset) continue;
                returnData.push({
                    symbol: symbol,
                    asset: currentAsset,
                    minItm: this.processMinPositionsItm(currentAsset.price, currentAsset.cached_stats?.lowest5_open_prices ?? []),
                    positions: byAsset[symbol],
                    // the API sends only both price edges of a long list, so the total comes with it
                    count: this.actionableCounts[symbol] ?? byAsset[symbol].length,
                })
            }
            
            return returnData;
        }
    },
};
</script>

<style scoped>


/* use `background` shorthand, not `background-color`: the theme's
   .btn-danger gradient (background-image) paints over background-color */
.buy-btn-elevated,
.buy-btn-elevated:hover,
.buy-btn-elevated:focus {
    background: #DB5D35 !important;
    border-color: #DE6642 !important;
}

.buy-btn-critical,
.buy-btn-critical:hover,
.buy-btn-critical:focus {
    background: #DB7A35 !important;
    border-color: #DE8342 !important;
}

.buy-btn-rule,
.buy-btn-rule:hover,
.buy-btn-rule:focus {
    background: #6f42c1 !important;
    border-color: #643ab0 !important;
}

</style>
